import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, CalendarCheck, CalendarDays, ClipboardList, Users, Package, Zap, Settings, Wallet, Coffee, ShoppingCart, PackageMinus } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useGreeting } from "../lib/useGreeting";
import { StreakBadge } from "../components/StreakBadge";
import TutorialDayCard from "../components/TutorialDayCard";
import ActionsNow from "../components/ActionsNow";
import WhiteboardBanner from "../components/WhiteboardBanner";

export default function HomePage() {
    const { user } = useAuth();
    const { phrase } = useGreeting(user);
    const [sites, setSites] = useState([]);
    const [streaks, setStreaks] = useState({}); // site_id → {streak_days, best_streak_days}
    const [loading, setLoading] = useState(true);
    const today = new Date();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/sites");
                setSites(data);
                // Best-effort: fetch streaks in parallel; ignore failures per site
                const ss = await Promise.allSettled(
                    (data || []).map((s) => api.get(`/sites/${s.id}/streak`).then((r) => ({ id: s.id, ...r.data })))
                );
                const map = {};
                ss.forEach((r) => { if (r.status === "fulfilled") map[r.value.id] = r.value; });
                setStreaks(map);
            } catch (e) {
                toast.error(apiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 fx-fade-in-up">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Today · {fmtDate(today.toISOString())}</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1" data-testid="home-greeting">{phrase || "Your sites"}</h1>
                    <p className="text-sm text-gray-500 mt-1">Tap a site to run today's checklist or open the monthly tracker.</p>
                </div>
            </div>

            <TutorialDayCard />
            <WhiteboardBanner />
            <ActionsNow />

            {loading ? (
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-40 rounded-2xl bg-white border border-gray-100 animate-pulse" />
                    ))}
                </div>
            ) : sites.length === 0 ? (
                <div className="mt-10 rounded-2xl bg-white border border-gray-100 p-10 text-center text-sm text-gray-500" data-testid="no-sites">
                    You are not assigned to any site yet.
                </div>
            ) : (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {sites.map((s, idx) => (
                        <SiteCard key={s.id} site={s} user={user} streak={streaks[s.id]} idx={idx} />
                    ))}
                </div>
            )}
        </div>
    );
}

const SITE_TYPE_META = {
    MANAGED_CLIENT: { label: "Managed Client", cls: "bg-[#EEF2F8] text-[#1E3355] border-[#C7D1E2]" },
    OWN_BUILDING:   { label: "Own Building",   cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    COWORKING:      { label: "Coworking",      cls: "bg-violet-50 text-violet-800 border-violet-200" },
};

function SiteCard({ site, user, streak, idx = 0 }) {
    const siteType = site.site_type || "MANAGED_CLIENT";
    const typeMeta = SITE_TYPE_META[siteType] || SITE_TYPE_META.MANAGED_CLIENT;
    const accent = site.accent_color || "#1E3355";
    const vm = site.visible_modules || {};
    const isDraft = siteType === "MANAGED_CLIENT" && site.name !== "SMIFS";
    const isOwn = siteType === "OWN_BUILDING";
    const isCoworking = siteType === "COWORKING";
    const tiles = [
        ...(isOwn ? [{ key: "building_ops", testid: "buildingops", to: `/buildings/${site.id}`, icon: Building2, primary: true, title: "Building operations" }] : []),
        ...(isCoworking ? [{ key: "coworking_ops", testid: "coworking", to: `/coworking/${site.id}`, icon: Coffee, primary: true, title: "Coworking sales & cash" }] : []),
        { key: "checklist",   testid: "run",         to: `/sites/${site.id}/run`,         icon: ClipboardList, primary: !isOwn && !isCoworking,  title: "Run today" },
        { key: "diary",       testid: "diary",       to: `/sites/${site.id}/diary`,       icon: CalendarDays,  title: "Diary" },
        { key: "attendance",  testid: "attendance",  to: `/sites/${site.id}/attendance`,  icon: Users,         title: "Attendance" },
        { key: "consumables", testid: "consumables", to: `/sites/${site.id}/consumables`, icon: Package,       title: "Consumables" },
        { key: "expenses",    testid: "expenses",    to: `/sites/${site.id}/expenses`,    icon: Wallet,        title: "Expenses" },
        { key: "requisition", testid: "requisition", to: `/sites/${site.id}/requisition`, icon: ShoppingCart,  title: "Monthly requisition" },
        { key: "issue_log",   testid: "issuelog",    to: `/sites/${site.id}/issue-log`,   icon: PackageMinus,  title: "Daily issue log" },
        { key: "usage_logs",  testid: "usage",       to: `/sites/${site.id}/usage`,       icon: Zap,           title: "Usage logs" },
        { key: "checklist_tracker", moduleKey: "checklist", testid: "tracker", to: `/sites/${site.id}/tracker`, icon: CalendarCheck, title: "Monthly tracker" },
    ].filter((t) => {
        const modKey = t.moduleKey || t.key;
        return vm[modKey] === undefined ? true : vm[modKey];
    });
    const cols = tiles.length || 1;
    return (
        <div
            className="fx-card fx-card-hover fx-accent-stripe fx-fade-in-up overflow-hidden flex flex-col"
            style={{ "--accent": accent, animationDelay: `${idx * 40}ms` }}
            data-testid={`site-card-${site.name}`}
        >
            <div className="p-5 pl-6 flex-1">
                <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: `${accent}12`, color: accent }}>
                            <Building2 className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="font-display font-bold text-lg text-[#1E3355] leading-tight">{site.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{site.address}</div>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border uppercase tracking-wider ${typeMeta.cls}`}
                            data-testid={`site-type-badge-${site.name}`}
                        >
                            {typeMeta.label}
                        </span>
                        {isDraft && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wider" data-testid={`draft-badge-${site.name}`}>
                                Draft
                            </span>
                        )}
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {streak?.streak_days > 0 && (
                        <StreakBadge days={streak.streak_days} best={streak.best_streak_days} testid={`streak-${site.name}`} />
                    )}
                    {site.floors?.length > 0 && (
                        <div className="text-xs text-gray-500">Floors {site.floors.join(", ")}{site.agreement_ref ? ` · ${site.agreement_ref}` : ""}</div>
                    )}
                </div>
            </div>
            <div
                className="grid divide-x divide-gray-100 border-t border-gray-100 bg-[#FAFBFC]"
                style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
            >
                {tiles.map((t) => {
                    const Icon = t.icon;
                    const cls = "fx-tap flex items-center justify-center gap-1 py-3 text-[11px] sm:text-sm font-medium text-gray-600 hover:text-[#1E3355] hover:bg-[#F4F6F9] transition-colors";
                    return (
                        <Link
                            key={t.key}
                            to={t.to}
                            className={cls}
                            style={t.primary ? { color: accent } : undefined}
                            data-testid={`site-${t.testid}-link-${site.name}`}
                            title={t.title}
                        >
                            <Icon className="w-4 h-4" />
                        </Link>
                    );
                })}
            </div>
            {user?.role === "HO_ADMIN" && (
                <div className="border-t border-gray-100 bg-white">
                    <Link
                        to={`/admin/sites/${site.id}/settings`}
                        className="flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium text-gray-500 hover:text-[#1E3355] hover:bg-[#F4F6F9] transition-colors"
                        data-testid={`site-settings-link-${site.name}`}
                    >
                        <Settings className="w-3.5 h-3.5" /> Site settings
                    </Link>
                </div>
            )}
        </div>
    );
}
